import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { AppInjector } from 'app/app.module';
import { DomHandlersService } from '../dom-handlers.service';
import { GlobalService } from '../global.service';
import { BlockTypeDto, findBlockType } from './block-types';
import { DomHelperService } from '../dom-helper.service';
import { Block } from 'app/interfaces/blocks';
import { ContentBoxSettingsService } from 'app/main/pages/general-editor-tabs/content-box-settings/content-box-setting.service';
import { ElementPosition, FieldBlockType } from 'app/main/pages/general-editor-tabs/content-tab/content-tab.dto';
import { SessionImageService } from '../session-image.service';
import { DomListenerService } from '../dom-listener.service';
import { EditTemplateFacade } from 'app/main/pages/edit-template/store/edit-template-store.facade';
import { take } from 'rxjs';

declare const $;
@Injectable({
    providedIn: 'root'
})
export class ContentDraggableService {
    private renderer: Renderer2;
    constructor(
        rendererFactory: RendererFactory2,
        private globalService: GlobalService,
        private domHelperService: DomHelperService,
        private contentBoxSettingsService: ContentBoxSettingsService,
        private sessionImageService: SessionImageService,
        private domListenerService: DomListenerService,
        private editTemplateFacade: EditTemplateFacade,
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }
    private blockType = <BlockTypeDto>{};
    /**
   * drag the content box
   */
    enable() {
        const _domHandlersService = AppInjector.get(DomHandlersService);
        $('.rowItem').draggable({
            cursorAt: { left: -3, top: -3 },
            handle: '.block-sort',
            cancel: '.stop-sorting',
            helper: (ele) => this.createHelper(ele, _domHandlersService),
            start: (ele) => this.onDragStart(ele),
            drag: () => this.onDrag(),
            stop: (ele) => this.onDragStop(ele),
        })
    }

    private createHelper(ele: Event, _domHandlersService: DomHandlersService) {
        const contentBox = (ele.currentTarget as HTMLElement).querySelector('.content-box-inner');
        const contentType = contentBox?.getAttribute('type');
        this.blockType = findBlockType(contentType);
        return $(_domHandlersService.iconBox(contentType, 'highlightSortBox'));
    }

    private onDragStart(ele: Event) {
        this.toggleClass('.ui-draggable-dragging', 'clone-show', true);
        this.toggleClass('.content-box-inner', 'content-box-hovered', true);
        this.globalService.initializeHoverOnDragStart({ block: this.blockType?.type });
        this.renderer.addClass(ele.currentTarget, 'opacity-50');
    }

    private onDrag() {
        const highlightSortBox = document.querySelector('.highlightSortBox');
        if (highlightSortBox) {
            this.renderer.addClass(highlightSortBox, 'highlightSortBoxActive');
        }
    }

    private onDragStop(ele: Event) {
        this.renderer.removeClass(ele.target, 'opacity-50');
        this.toggleClass('.ui-draggable-dragging', 'clone-show', false);
        this.toggleClass('.content-box-inner', 'content-box-hovered', false);

        const position = document.querySelector('.template-page')?.getAttribute('position');
        const contentBoxEnter = document.querySelector('.content-box-enter');
        if (contentBoxEnter) {
            const rowItem = contentBoxEnter.querySelector('.content-box-row');
            this.handleDrop(position, rowItem, ele);
            this.cleanupEmptyContentBoxes();
        }
        this.cleanupDragArtifacts();
        this.globalService.contentBoxSort();
        this.blockType = <BlockTypeDto>{};
        this.initializeEventListeners();
    }

    private handleDrop(position: string | null, rowItem: Element | null, ele: Event) {
        if (position === ElementPosition.Left && rowItem) {
            this.renderer.insertBefore(rowItem, ele.target, rowItem.firstChild);
        } else if (position === ElementPosition.Right && rowItem) {
            this.renderer.appendChild(rowItem, ele.target);
        } else {
            this.insertContentBox(ele, position);
        }
        this.renderer.removeAttribute(ele.target, 'data-resized');
    }

    private insertContentBox(ele: Event, position:string) {
        const dropIndicator = document.querySelector('.dropIndicator-bottom');
        const parentContentBox = dropIndicator?.closest('.content-box');
        const contentBoxRowParent = (ele.target as HTMLElement).closest('.content-box-row');
        if ([FieldBlockType.ContentBox, FieldBlockType.ImageBlock, FieldBlockType.ContentFields].includes(this.blockType?.type) && parentContentBox) {
            const indicator = document.querySelector('.content-box-enter')?.querySelector('[class^="dropIndicator-"]') as HTMLElement;
            const newContentBox = this.createNewContentBox((ele.target as HTMLElement));
            if(position === ElementPosition.Top){
                this.renderer.insertBefore(parentContentBox.parentNode, newContentBox, parentContentBox);
            }else if(indicator?.classList?.contains('dropIndicator-inner-bottom')){
                this.globalService.onDropOnInnerIndicator(indicator, newContentBox);
            }else{
                newContentBox?.querySelector('.rowItem').classList.remove('merged-row-item');
                this.renderer.insertBefore(parentContentBox.parentNode, newContentBox, parentContentBox.nextSibling);
            }
            this.adjustTheBackgroundImage();
            // parentContentBox.remove();
        } else if (parentContentBox) {
            if(position === ElementPosition.Top){
                this.renderer.insertBefore(parentContentBox.parentNode, contentBoxRowParent.closest('.content-box'), parentContentBox);
            }else{
                this.renderer.insertBefore(parentContentBox.parentNode, contentBoxRowParent.closest('.content-box'), parentContentBox.nextSibling);
            }
        }
    }

    private adjustTheBackgroundImage(){
        this.editTemplateFacade.templateRoute$.pipe(take(1)).subscribe((templateRoute) => {
            document.querySelectorAll('.content-editor-block').forEach((ele:HTMLElement)=>{
                const contentBox = ele.querySelectorAll('.content-box-inner[type="content box"]');
                if(contentBox.length > 0){
                    const uuid = contentBox[0].getAttribute('editor-uuid');
                    const blockData:Block = this.globalService.fields_data['blocks'][uuid];
                    if(blockData?.background_image){
                        const contentBoxImage = this.sessionImageService.getTemplateImages(blockData.background_image, Number(templateRoute.id), this.globalService.getTheSectionByRoute(templateRoute.route).toLowerCase());
                        this.contentBoxSettingsService.setContentBoxImage(uuid, blockData.background_image, true, contentBoxImage);  
                    }
                }else{
                    if(ele.querySelector('.content-box-inner')){
                        const uuid = ele.querySelector('.content-box-inner').getAttribute('editor-uuid');
                        this.contentBoxSettingsService.removeContentBoxIamge(uuid);
                    }
                }
            })
        });
    }

    private createNewContentBox(target: HTMLElement) {
        const getTheLenthOfRows = (target as HTMLElement).closest('.content-box-row')
        if(getTheLenthOfRows.querySelectorAll('.rowItem').length === 1){
            return target.closest('.content-box');
        }
        const editorInstance = this.renderer.createElement('div');
        this.renderer.addClass(editorInstance, 'content-box');
        this.renderer.addClass(editorInstance, 'content-box-block');
        this.renderer.addClass(editorInstance, this.blockType?.class);
        this.renderer.addClass(editorInstance, 'marginminus70');

        const sortHelperDiv = this.renderer.createElement('div');
        this.renderer.addClass(sortHelperDiv, 'sort-helper');
        this.renderer.appendChild(editorInstance, sortHelperDiv);

        const contentBoxRowDiv = this.renderer.createElement('div');
        this.renderer.addClass(contentBoxRowDiv, 'content-box-row');
        this.renderer.appendChild(contentBoxRowDiv, target);

        this.renderer.appendChild(editorInstance, contentBoxRowDiv);
        return editorInstance;
    }

    private cleanupEmptyContentBoxes() {
        const contentBoxRowParent = document.querySelectorAll('.content-box-block');
        contentBoxRowParent.forEach((contentBox:HTMLElement) => {
           if (contentBox && contentBox.querySelectorAll('.rowItem').length === 0) {
                contentBox?.remove();
            } 
        });
    }

    private cleanupDragArtifacts() {
        ['.dropIndicator-top', '.dropIndicator-bottom', '.dropIndicator-left', '.dropIndicator-right'].forEach(selector => {
            document.querySelector(selector)?.remove();
        });
        document.querySelector('.template-page')?.removeAttribute('position');
        document.querySelectorAll('.merged-row').forEach((mergedRow:HTMLElement) => {
            if(mergedRow.querySelectorAll('.merged-row-item').length === 0){
                this.renderer.removeChild(mergedRow.parentNode, mergedRow);
            }
        });
        setTimeout(() => {
            this.globalService.adjustInnerContentBoxWidthOnAdd(true);
        }, 0);
    }

    private initializeEventListeners() {
        this.domHelperService.initializeEventListener('.content-field-inner', 'click', 'openFieldSettings');
        this.domHelperService.initializeEventListener('.content-box', 'mouseenter', 'mouseEnterOnContentBox');
        this.domHelperService.initializeEventListener('.content-box', 'mouseleave', 'mouseLeaveOnContentBox');
        this.domHelperService.initializeEventListener('.rowItem', 'mouseenter', 'mouseEnterOnContentBoxInner');
        this.domHelperService.initializeEventListener('.rowItem', 'mouseleave', 'mouseLeaveOnContentBoxInner');
        const contentBoxEnter = document.querySelectorAll('.content-box-enter');
        if(contentBoxEnter.length){
            contentBoxEnter.forEach((contentEnter:HTMLElement) => {
                this.renderer.removeClass(contentEnter, 'content-box-enter');
            })
        }
        document.querySelectorAll('.dropIndicator-right, .dropIndicator-left, .dropIndicator-bottom, .dropIndicator-top, .dropIndicator-inner-bottom').forEach((ele:HTMLElement) => {
            this.domListenerService.removeListener(ele, 'mousemove');
        });
        document.querySelectorAll('.content-box-inner').forEach((contentBoxInner:HTMLElement) => {
          this.domListenerService.removeListener(contentBoxInner, 'mouseenter');
        });
    }

    private toggleClass(selector: string, className: string, add: boolean) {
        document.querySelectorAll(selector).forEach((element) => {
            if (add) {
                this.renderer.addClass(element, className);
            } else {
                this.renderer.removeClass(element, className);
            }
        });
    }
}